import { ICampaignEditForm, ICampaignTargetingListing } from '../models/campaign.model';
import { ICampaignCreateForm, CampaignModel } from '../models/campaign.model';
import { ask } from '@monorepo/tools/src/lib/tools/ask/ask';
import endpoints from './endpoints/index.endpoints';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { IRequestOptions } from '@monorepo/tools/src/lib/interfaces/url';

function normalizePayoutResponse(response: unknown): number | undefined {
	if (typeof response === 'number' && !Number.isNaN(response)) {
		return response;
	}

	if (response && typeof response === 'object') {
		const record = response as Record<string, unknown>;
		const defaultPayout = record.default_payout ?? record.defaultPayout;
		const payout = record.payout;

		if (typeof defaultPayout === 'number' && !Number.isNaN(defaultPayout)) {
			return defaultPayout;
		}

		if (typeof payout === 'number' && !Number.isNaN(payout)) {
			return payout;
		}
	}

	return undefined;
}

export const CampaignsApi = {
	list: (params?: URLSearchParams): Promise<CampaignModel[]> => {
		return ask.get(endpoints.campaigns.list(), params);
	},
	create: (campaign: ICampaignCreateForm & ICampaignTargetingListing, options?: IRequestOptions): Promise<{ id: id }> => {
		return ask.post<ICampaignCreateForm & ICampaignTargetingListing>(endpoints.campaigns.create(), campaign, options);
	},
	edit: (campaignId: id, campaign: ICampaignEditForm, options?: IRequestOptions): Promise<null> => {
		return ask.patch<ICampaignEditForm>(endpoints.campaigns.edit(campaignId), campaign, options);
	},
	editTargetListings: (campaignId: id, targetingListing: ICampaignTargetingListing, options?: IRequestOptions): Promise<null> => {
		return ask.put<ICampaignTargetingListing>(endpoints.campaigns.targetListings(campaignId), targetingListing, options);
	},
	get: (campaignId: id, params?: URLSearchParams): Promise<CampaignModel> => {
		return ask.get(endpoints.campaigns.get(campaignId), params);
	},
	delete: (campaignId: id) => {
		return ask.delete(endpoints.campaigns.delete(campaignId));
	},
	addCreatives: (campaignId: id, creativeIds: id[], options?: IRequestOptions): Promise<null> => {
		return ask.post(endpoints.campaigns.addAndEditCreatives(campaignId), creativeIds, options);
	},
	editCreatives: (campaignId: id, creativeIds: id[], options?: IRequestOptions): Promise<null> => {
		return ask.put(endpoints.campaigns.addAndEditCreatives(campaignId), creativeIds, options);
	},
	enableCampaigns(campaignIds: id[]): Promise<null> {
		return ask.patch(endpoints.campaigns.enableCampaigns(), campaignIds);
	},
	pauseCampaigns(campaignIds: id[]): Promise<null> {
		return ask.patch(endpoints.campaigns.pauseCampaigns(), campaignIds);
	},
	attachDeals(campaignId: id, dealIds: id[]): Promise<null> {
		return ask.put(endpoints.campaigns.attachDeals(campaignId), dealIds);
	},
	getTargets(): Promise<null> {
		return ask.get(endpoints.campaigns.targetList());
	},
	getAmountOfSharedTarget(id: id): Promise<CampaignModel[]> {
		return ask.get(endpoints.campaigns.sharedTargetAmount(id));
	},
	downloadBidKeys: (id: id, options?: URLSearchParams): Promise<any> => {
		return ask.download(endpoints.campaigns.bidKeys(id), options);
	},
	uploadBidKeys: (id: id, options?: any): Promise<any> => {
		const formData = new FormData();
		formData.append('file', options);

		return ask.upload(endpoints.campaigns.uploadbidKeys(id), formData);
	},
	downloadKeysInTarget: (id: id, options?: URLSearchParams): Promise<any> => {
		return ask.download(endpoints.campaigns.sitesInTarget(id), options);
	},
	uploadKeysInTarget: (id: id, options?: any): Promise<any> => {
		const formData = new FormData();
		formData.append('file', options);

		return ask.upload(endpoints.campaigns.uploadSitesInTarget(id), formData);
	},
	getPayout: (params: { geo: string; destinationId: id; webappsCidId?: id }): Promise<number | undefined> => {
		const searchParams = new URLSearchParams();
		searchParams.set('geo', params.geo);
		searchParams.set('destinationId', String(params.destinationId));

		if (params.webappsCidId != null && params.webappsCidId !== '') {
			searchParams.set('webappsCidId', String(params.webappsCidId));
		}

		return ask.get(endpoints.campaigns.payout(), searchParams).then(normalizePayoutResponse);
	},
};
